import { DialogConfig } from '@agent-ds/shared/components/dialog/dialog-config';
import { DialogRef } from '@agent-ds/shared/components/dialog/dialog-ref';
import { Component, HostListener } from '@angular/core';
import '@agent-ds/shared/util/date';

export const IS2ND_MATCH_VALID_VALUE = '有効';
export const FILTER_NULL_LABEL = '---';
export const VALID_DOCUMENT_STATUSES = ['有', 'OM有', 'キャリアノート'];
export const ACADEMIC_FIELD_LIBERAL = '文系';
export const ACADEMIC_FIELD_SCIENCE = '理系';

export type DatePeriod =
  | 'last-week'
  | 'this-week'
  | 'next-week'
  | 'last-month'
  | 'this-month'
  | 'next-month'
  | 'yesterday'
  | 'today'
  | 'tomorrow'
  | 'last-year'
  | 'this-year'
  | 'next-year';

export type FieldType =
  | 'interviewDate'
  | 'expectedCompanySeminarDate'
  | 'expectedContractMonth'
  | 'contactDate'
  | 'check5Introduce'
  | 'check5Apply'
  | 'mypageLoggedInAt';

export function resolveDatePeriod(period: DatePeriod): { from: string; to: string } | null {
  let range: { from: Date; to: Date } | null = null;
  switch (period) {
    case 'last-week':
      range = Date.lastWeek();
      break;
    case 'this-week':
      range = Date.currentWeek();
      break;
    case 'next-week':
      range = Date.nextWeek();
      break;
    case 'last-month':
      range = Date.lastMonth();
      break;
    case 'this-month':
      range = Date.currentMonth();
      break;
    case 'next-month':
      range = Date.nextMonth();
      break;
    case 'yesterday':
      range = Date.yesterday();
      break;
    case 'today':
      range = Date.today();
      break;
    case 'tomorrow':
      range = Date.tomorrow();
      break;
    case 'last-year':
      range = Date.lastYear(true);
      break;
    case 'this-year':
      range = Date.currentYear(true);
      break;
    case 'next-year':
      range = Date.nextYear(true);
      break;
    default:
      return null;
  }
  if (!range) {
    return null;
  }
  return {
    from: range.from.toAsialDateString(true),
    to: range.to.toAsialDateString(true),
  };
}

export function resolveMonthPeriod(period: DatePeriod): { from: string; to: string } | null {
  const fmt = (d: Date): string => d.toAsialDateString(true).slice(0, 7);
  switch (period) {
    case 'last-month': {
      const m = fmt(Date.lastMonth().from);
      return { from: m, to: m };
    }
    case 'this-month': {
      const m = fmt(Date.currentMonth().from);
      return { from: m, to: m };
    }
    case 'next-month': {
      const m = fmt(Date.nextMonth().from);
      return { from: m, to: m };
    }
    case 'last-year': {
      const r = Date.lastYear(true);
      return { from: fmt(r.from), to: fmt(r.to) };
    }
    case 'this-year': {
      const r = Date.currentYear(true);
      return { from: fmt(r.from), to: fmt(r.to) };
    }
    case 'next-year': {
      const r = Date.nextYear(true);
      return { from: fmt(r.from), to: fmt(r.to) };
    }
    default:
      return null;
  }
}

export interface CheaderFilter {
  activeCountMin: number | null;
  activeCountMax: number | null;
  progressCountMin: number | null;
  progressCountMax: number | null;
  activeDayChangeMin: number | null;
  activeDayChangeMax: number | null;
  progressDayChangeMin: number | null;
  progressDayChangeMax: number | null;
  studentName: string;
  graduateYears: string[];
  academicFieldType: string | null;
  universityLevels: string[];
  interviewDateFrom: string;
  interviewDateTo: string;
  interviewDatePeriod: DatePeriod | null;
  sfRanks: string[];
  enterpriseName: string;
  expectedCompanySeminarDateFrom: string;
  expectedCompanySeminarDateTo: string;
  expectedCompanySeminarDatePeriod: DatePeriod | null;
  expectedContractMonthFrom: string;
  expectedContractMonthTo: string;
  expectedContractMonthPeriod: DatePeriod | null;
  contactDateFrom: string;
  contactDateTo: string;
  contactDatePeriod: DatePeriod | null;
  is2ndMatchValue: string | null;
  check5IntroduceFrom: string;
  check5IntroduceTo: string;
  check5IntroducePeriod: DatePeriod | null;
  check5ApplyFrom: string;
  check5ApplyTo: string;
  check5ApplyPeriod: DatePeriod | null;
  mypageLoggedInAtFrom: string;
  mypageLoggedInAtTo: string;
  mypageLoggedInAtPeriod: DatePeriod | null;
  documentStatus: string | null;
  caUserIds: number[];
}

export const EMPTY_CHEADER_FILTER: CheaderFilter = {
  activeCountMin: null,
  activeCountMax: null,
  progressCountMin: null,
  progressCountMax: null,
  activeDayChangeMin: null,
  activeDayChangeMax: null,
  progressDayChangeMin: null,
  progressDayChangeMax: null,
  studentName: '',
  graduateYears: [],
  academicFieldType: null,
  universityLevels: [],
  interviewDateFrom: '',
  interviewDateTo: '',
  interviewDatePeriod: null,
  sfRanks: [],
  enterpriseName: '',
  expectedCompanySeminarDateFrom: '',
  expectedCompanySeminarDateTo: '',
  expectedCompanySeminarDatePeriod: null,
  expectedContractMonthFrom: '',
  expectedContractMonthTo: '',
  expectedContractMonthPeriod: null,
  contactDateFrom: '',
  contactDateTo: '',
  contactDatePeriod: null,
  is2ndMatchValue: null,
  check5IntroduceFrom: '',
  check5IntroduceTo: '',
  check5IntroducePeriod: null,
  check5ApplyFrom: '',
  check5ApplyTo: '',
  check5ApplyPeriod: null,
  mypageLoggedInAtFrom: '',
  mypageLoggedInAtTo: '',
  mypageLoggedInAtPeriod: null,
  documentStatus: null,
  caUserIds: [],
};

interface CustomerManagementFilterDialogData {
  currentFilter: CheaderFilter;
  caUserList: { id: number; name: string }[];
  isMgrMode: boolean;
}

@Component({
  selector: 'ag-customer-management-filter-dialog',
  templateUrl: './customer-management-filter-dialog.component.html',
  styleUrls: ['./customer-management-filter-dialog.component.scss'],
})
export class CustomerManagementFilterDialogComponent {
  readonly UNIVERSITY_LEVELS = ['S', 'A', 'B', 'C', 'D', 'E'];
  readonly SF_RANKS = ['A', 'B', 'C1', 'C2', 'D1', 'D2', 'E'];
  readonly ACADEMIC_FIELD_TYPES = [ACADEMIC_FIELD_LIBERAL, ACADEMIC_FIELD_SCIENCE];
  readonly IS2ND_MATCH_VALUES = [IS2ND_MATCH_VALID_VALUE, FILTER_NULL_LABEL];
  readonly DOCUMENT_STATUSES = [...VALID_DOCUMENT_STATUSES, FILTER_NULL_LABEL];

  readonly DATE_RANGE_PERIODS: { key: DatePeriod; label: string }[] = [
    { key: 'last-week', label: '先週' },
    { key: 'this-week', label: '今週' },
    { key: 'next-week', label: '来週' },
    { key: 'last-month', label: '先月' },
    { key: 'this-month', label: '今月' },
    { key: 'next-month', label: '来月' },
  ];

  readonly DATE_OTHER_PERIODS: { key: DatePeriod; label: string }[] = [
    { key: 'yesterday', label: '昨日' },
    { key: 'today', label: '今日' },
    { key: 'tomorrow', label: '明日' },
    { key: 'last-year', label: '昨年' },
    { key: 'this-year', label: '今年' },
    { key: 'next-year', label: '来年' },
  ];

  readonly MONTH_RANGE_PERIODS: { key: DatePeriod; label: string }[] = [
    { key: 'last-month', label: '先月' },
    { key: 'this-month', label: '今月' },
    { key: 'next-month', label: '来月' },
  ];

  readonly MONTH_OTHER_PERIODS: { key: DatePeriod; label: string }[] = [
    { key: 'last-year', label: '昨年' },
    { key: 'this-year', label: '今年' },
    { key: 'next-year', label: '来年' },
  ];

  private readonly DATE_OTHER_KEYS = new Set<DatePeriod>(['yesterday', 'today', 'tomorrow', 'last-year', 'this-year', 'next-year']);
  private readonly MONTH_OTHER_KEYS = new Set<DatePeriod>(['last-year', 'this-year', 'next-year']);

  readonly graduateYearOptions: string[] = (() => {
    const maxYear = new Date().getFullYear() + 2;
    return Array.from(
      { length: maxYear - 1990 + 1 },
      (_, i) => String(maxYear - i),
    );
  })();
  caUserList: { id: number; name: string }[];
  isMgrMode: boolean;
  filterDraft: CheaderFilter;
  otherDropdownOpen: { [K in FieldType]?: boolean } = {};

  constructor(public dialog: DialogRef, public config: DialogConfig<CustomerManagementFilterDialogData>) {
    const data = config.data!;
    this.caUserList = data.caUserList;
    this.isMgrMode = data.isMgrMode;
    const currentFilter = data.currentFilter;
    this.filterDraft = {
      ...currentFilter,
      graduateYears: currentFilter.graduateYears.slice(),
      universityLevels: currentFilter.universityLevels.slice(),
      sfRanks: currentFilter.sfRanks.slice(),
      caUserIds: currentFilter.caUserIds.slice(),
    };
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.otherDropdownOpen = {};
  }

  setDatePeriod(field: FieldType, period: DatePeriod): void {
    const range = resolveDatePeriod(period);
    this.applyPeriod(field, period, range);
  }

  setMonthPeriod(field: FieldType, period: DatePeriod): void {
    const range = resolveMonthPeriod(period);
    this.applyPeriod(field, period, range);
  }

  clearPeriod(field: FieldType): void {
    (this.filterDraft as any)[field + 'Period'] = null;
  }

  toggleOtherDropdown(field: FieldType, event: MouseEvent): void {
    event.stopPropagation();
    const wasOpen = !!this.otherDropdownOpen[field];
    this.otherDropdownOpen = wasOpen ? {} : { [field]: true };
  }

  selectOtherPeriod(field: FieldType, period: DatePeriod, isMonth: boolean): void {
    if (isMonth) {
      this.setMonthPeriod(field, period);
    } else {
      this.setDatePeriod(field, period);
    }
    this.otherDropdownOpen = {};
  }

  isOtherPeriodSelected(field: FieldType, isMonth: boolean): boolean {
    const period = (this.filterDraft as any)[field + 'Period'] as DatePeriod | null | undefined;
    if (!period) {
      return false;
    }
    return isMonth ? this.MONTH_OTHER_KEYS.has(period) : this.DATE_OTHER_KEYS.has(period);
  }

  getOtherPeriodLabel(field: FieldType, isMonth: boolean): string {
    const period = (this.filterDraft as any)[field + 'Period'] as DatePeriod | null | undefined;
    if (!period) {
      return 'その他';
    }
    const periods = isMonth ? this.MONTH_OTHER_PERIODS : this.DATE_OTHER_PERIODS;
    const found = periods.find((p) => p.key === period);
    return found ? found.label : 'その他';
  }

  close(): void {
    this.dialog.close();
  }

  clearFilter(): void {
    this.dialog.close({
      ...EMPTY_CHEADER_FILTER,
      graduateYears: [],
      universityLevels: [],
      sfRanks: [],
      caUserIds: [],
    });
  }

  applyFilter(): void {
    this.dialog.close(this.cloneFilterDraft());
  }

  saveFilter(): void {
    this.dialog.close({ ...this.cloneFilterDraft(), _saveRequested: true } as any);
  }

  toggleInFilter<T>(array: T[], value: T): T[] {
    return array.includes(value)
      ? array.filter((v) => v !== value)
      : array.concat([value]);
  }

  isInFilter(array: any[], value: any): boolean {
    return array.includes(value);
  }

  private cloneFilterDraft(): CheaderFilter {
    return {
      ...this.filterDraft,
      graduateYears: this.filterDraft.graduateYears.slice(),
      universityLevels: this.filterDraft.universityLevels.slice(),
      sfRanks: this.filterDraft.sfRanks.slice(),
      caUserIds: this.filterDraft.caUserIds.slice(),
    };
  }

  private applyPeriod(field: FieldType, period: DatePeriod, range: { from: string; to: string } | null): void {
    if ((this.filterDraft as any)[field + 'Period'] === period) {
      // 既に設定済みの場合はクリアする
      (this.filterDraft as any)[field + 'Period'] = null;
      (this.filterDraft as any)[field + 'From'] = '';
      (this.filterDraft as any)[field + 'To'] = '';
      return;
    }

    (this.filterDraft as any)[field + 'Period'] = period;
    if (range) {
      (this.filterDraft as any)[field + 'From'] = range.from;
      (this.filterDraft as any)[field + 'To'] = range.to;
    }
  }
}
